import { TextInput, Combobox, useCombobox, Text } from '@mantine/core'
import { useQuery, useLazyQuery } from '@apollo/client/react'
import { useMemo, useCallback, useState, useRef, forwardRef, useImperativeHandle, useEffect } from 'react'
import AllTags from '../graphql/queries/AllTags'
import TrackCount from '../graphql/queries/TrackCount'

const TagStringEditor = forwardRef(({ roomId, value, onChange, ...props }, ref) => {
    const { data, error } = useQuery(AllTags, {
        variables: { roomId }
    })

    const [getTrackCount, { data: trackCountData, loading: trackCountLoading }] = useLazyQuery(TrackCount, {
        fetchPolicy: 'network-only'
    })

    const [cursorPosition, setCursorPosition] = useState(0)
    const inputRef = useRef(null)
    const debounceTimerRef = useRef(null)
    const combobox = useCombobox({
        onDropdownClose: () => combobox.resetSelectedOption(),
    })

    // Debounced track count query
    useEffect(() => {
        // Clear existing timer
        if (debounceTimerRef.current) {
            clearTimeout(debounceTimerRef.current)
        }

        // Query if we have a non-empty value (roomId is optional)
        if (value && value.trim()) {
            debounceTimerRef.current = setTimeout(() => {
                getTrackCount({
                    variables: {
                        roomId,
                        tags: value.trim()
                    }
                })
            }, 500) // 500ms debounce
        }

        // Cleanup on unmount
        return () => {
            if (debounceTimerRef.current) {
                clearTimeout(debounceTimerRef.current)
            }
        }
    }, [roomId, value, getTrackCount])

    // Expose focus and other methods to parent components via ref
    useImperativeHandle(ref, () => ({
        focus: () => {
            inputRef.current?.focus()
        },
        setSelectionRange: (start, end) => {
            inputRef.current?.setSelectionRange(start, end)
        }
    }))

    // Log any query errors
    if (error) {
        console.error('TagStringEditor - AllTags query error:', error)
    }

    // Parse the current word being typed at cursor position
    const getCurrentWord = useCallback((text, position) => {
        if (!text) return { word: '', start: 0, end: 0 }

        // Find word boundaries (operators: or, not, and, parentheses, spaces)
        const beforeCursor = text.slice(0, position)
        const afterCursor = text.slice(position)

        // Match word boundary before cursor
        const beforeMatch = beforeCursor.match(/[^\s()[\]]*$/)
        const start = beforeMatch ? position - beforeMatch[0].length : position

        // Match word boundary after cursor
        const afterMatch = afterCursor.match(/^[^\s()[\]]*/);
        const end = afterMatch ? position + afterMatch[0].length : position

        const word = text.slice(start, end)
        return { word, start, end }
    }, [])

    // Get current word being edited
    const currentWordInfo = useMemo(() => {
        return getCurrentWord(value || '', cursorPosition)
    }, [value, cursorPosition, getCurrentWord])

    // Filter tags based on current word
    const suggestions = useMemo(() => {
        if (!data?.allTags || !Array.isArray(data.allTags)) return []

        const searchTerm = currentWordInfo.word.toLowerCase()

        // Return empty if no current word
        if (!searchTerm) return []

        // Skip if the word is an operator
        if (['or', 'and', 'not'].includes(searchTerm)) return []

        return data.allTags
            .filter(({ tag }) => tag.toLowerCase().includes(searchTerm))
            .sort((a, b) => {
                // Sort by: exact match first, then starts with, then by count
                const aLower = a.tag.toLowerCase()
                const bLower = b.tag.toLowerCase()

                if (aLower === searchTerm) return -1
                if (bLower === searchTerm) return 1

                const aStarts = aLower.startsWith(searchTerm)
                const bStarts = bLower.startsWith(searchTerm)

                if (aStarts && !bStarts) return -1
                if (!aStarts && bStarts) return 1

                return b.count - a.count
            })
            .slice(0, 10)
            .map(({ tag, count }) => ({
                value: tag,
                label: `${tag} (${count})`
            }))
    }, [data?.allTags, currentWordInfo.word])

    // Handle selection from dropdown
    const handleSelect = useCallback((selectedTag) => {
        if (!selectedTag || !onChange) return

        const { start, end } = currentWordInfo
        const before = (value || '').slice(0, start)
        const after = (value || '').slice(end)
        const newValue = before + selectedTag + after
        const newCursor = start + selectedTag.length

        onChange(newValue)
        combobox.closeDropdown()

        // Update cursor position after state update
        setTimeout(() => {
            if (inputRef.current) {
                inputRef.current.focus()
                inputRef.current.setSelectionRange(newCursor, newCursor)
                setCursorPosition(newCursor)
            }
        }, 0)
    }, [currentWordInfo, value, onChange, combobox])

    // Handle text input changes
    const handleChange = useCallback((event) => {
        const newValue = event.currentTarget.value
        onChange?.(newValue)
        combobox.openDropdown()
        combobox.updateSelectedOptionIndex('active')
    }, [onChange, combobox])

    // Track cursor position
    const handleKeyUp = useCallback((e) => {
        setCursorPosition(e.target.selectionStart)
    }, [])

    const handleKeyDown = useCallback((e) => {
        if (e.key === 'Enter') {
            combobox.closeDropdown()
        }
    }, [combobox])

    const handleClick = useCallback((e) => {
        setCursorPosition(e.target.selectionStart)
    }, [])

    const handleFocus = useCallback((e) => {
        setCursorPosition(e.target.selectionStart)
        if (suggestions.length > 0) {
            combobox.openDropdown()
        }
    }, [suggestions.length, combobox])

    const handleBlur = useCallback(() => {
        combobox.closeDropdown()
    }, [combobox])

    const options = suggestions.map((item) => (
        <Combobox.Option value={item.value} key={item.value}>
            {item.label}
        </Combobox.Option>
    ))

    // Track count display - more concise
    const trackCount = trackCountData?.trackCount
    const showTrackCount = value && value.trim() && !trackCountLoading && trackCount !== undefined

    const rightSection = showTrackCount ? (
        <Text
            size="xs"
            c="gray.5"
            style={{
                whiteSpace: 'nowrap',
                position: 'absolute',
                right: '8px',
                top: '50%',
                transform: 'translateY(-50%)',
                pointerEvents: 'none',
                backgroundColor: 'var(--mantine-color-dark-6)',
                paddingLeft: '8px'
            }}
        >
            {trackCount === 0 ? '0 tracks' : `${trackCount.toLocaleString()} track${trackCount !== 1 ? 's' : ''}`}
        </Text>
    ) : null

    return (
        <Combobox
            store={combobox}
            onOptionSubmit={handleSelect}
            withinPortal
        >
            <Combobox.Target>
                <TextInput
                    ref={inputRef}
                    value={value || ''}
                    onChange={handleChange}
                    onKeyDown={handleKeyDown}
                    onKeyUp={handleKeyUp}
                    onClick={handleClick}
                    onFocus={handleFocus}
                    onBlur={handleBlur}
                    placeholder="Enter tags (e.g., 'pop rock', 'jazz or classical', 'happy (rock or pop)')"
                    rightSection={rightSection}
                    {...props}
                />
            </Combobox.Target>

            <Combobox.Dropdown style={{ zIndex: 10000 }}>
                <Combobox.Options>
                    {options.length > 0 ? options : <Combobox.Empty>No tags found</Combobox.Empty>}
                </Combobox.Options>
            </Combobox.Dropdown>
        </Combobox>
    )
})

TagStringEditor.displayName = 'TagStringEditor'

export default TagStringEditor

